/**
 * Image parallax — shared front-end component.
 *
 * Drives the "background image drifts a few percent as the block passes through
 * the viewport" effect used by the intro heroes (and any future block/media that
 * opts in). The heavy lifting is a rAF-throttled scroll handler that only runs
 * while the block is on screen; an IntersectionObserver gates it so no scroll
 * math happens once the element has scrolled away. Scroll/resize are sourced
 * from the page-wide window.wombat.scrollManager/resizeManager (set up in
 * front-scripts.js, part of the site-wide main.bundle.js — required, no
 * fallback), so multiple parallax blocks on one page share a single native
 * listener each instead of adding one per block. `prefers-reduced-motion` is
 * honored by not initialising at all — the block keeps whatever static CSS
 * scale headroom it renders with for the no-JS / reduced-motion state.
 *
 * Two ways to use it. Explicit per-block wiring is what the existing heroes
 * need; the convention-based scan lets any future block opt in with markup only.
 *
 * The one thing a consuming block owes the component is that its image covers
 * the box it sits in at rest — `height: 100%` + `object-fit: cover`, not an
 * `auto` height, which sizes the box from the file's own ratio and leaves a
 * landscape photo short of a taller frame. The drift is then measured against
 * that image box (see measure()), so frame padding, inner cards and odd upload
 * ratios can no longer push the travel past the headroom.
 *
 * @example
 * // 1. Explicit, per-block:
 * import { initImageParallax } from '../../js/front/components/parallax';
 *
 * document
 *   .querySelectorAll( '.b-intro-star-hero.has-parallax' )
 *   .forEach( ( block ) =>
 *     initImageParallax( block, { imageSelector: '.b-intro-star-hero__image' } ),
 *   );
 *
 * @example
 * // 2. Convention-based scan (zero bespoke JS for a new block):
 * // <div class="js-parallax" data-parallax-image=".my-block__image" data-parallax-speed="2">
 * //   <img class="my-block__image" ... />
 * // </div>
 * import { initParallaxScan } from '../../js/front/components/parallax';
 * initParallaxScan();
 */

/**
 * Drift amount per speed level: the fraction of the block height the image
 * travels across the full scroll. Level 2 is the default feel; a block's SCSS
 * base `scale()` should match its default level's headroom so the reduced-motion
 * / no-JS state never reveals a gap.
 *
 * @type {Object<number, number>}
 */
export const PARALLAX_FACTORS = { 1: 0.07, 2: 0.13, 3: 0.2 };

/**
 * Speed level used when the block declares none.
 *
 * @type {number}
 */
export const DEFAULT_PARALLAX_LEVEL = 2;

/**
 * Whether the visitor asked the OS to minimise motion.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Resolve the element that should drift within a block.
 *
 * Priority: an explicit element, then an explicit selector, then the block's
 * `data-parallax-image` selector, then a child flagged `[data-parallax-image]`,
 * then the `.js-parallax-image` convention class.
 *
 * @param {HTMLElement} block   The block root element.
 * @param {Object}      options Resolution options (see initImageParallax).
 * @return {HTMLElement|null} The drifting element, or null when none matched.
 */
const resolveImage = ( block, options ) => {

	if ( options.image ) {

		return options.image;

	}

	const selector = options.imageSelector || block.dataset.parallaxImage;

	if ( selector ) {

		return block.querySelector( selector );

	}

	return (
		block.querySelector( '[data-parallax-image]' ) ||
		block.querySelector( '.js-parallax-image' )
	);

};

/**
 * Resolve the drift factor for a block from its speed level.
 *
 * @param {HTMLElement} block   The block root element.
 * @param {Object}      options Resolution options (see initImageParallax).
 * @return {number} The drift factor.
 */
const resolveFactor = ( block, options ) => {

	const factors = options.factors || PARALLAX_FACTORS;
	const defaultLevel = options.defaultLevel || DEFAULT_PARALLAX_LEVEL;
	const level = options.speed || block.dataset.parallaxSpeed || defaultLevel;

	return factors[ level ] || factors[ defaultLevel ];

};

/**
 * Attach the background-image parallax drift to a single block/image pair.
 *
 * @param {HTMLElement} block                   The scrolling root element.
 * @param {Object}      [options]               Configuration.
 * @param {string}      [options.imageSelector] Selector for the drifting child.
 * @param {HTMLElement} [options.image]         Explicit child; wins over imageSelector.
 * @param {Object}      [options.factors]       Level→factor map overriding the default.
 * @param {number}      [options.speed]         Level override for data-parallax-speed.
 * @param {number}      [options.defaultLevel]  Fallback level when none is set.
 * @return {{ destroy: Function }|null} Handle exposing destroy(), or null when not initialised (no image, or reduced motion).
 */
export const initImageParallax = ( block, options = {} ) => {

	const image = resolveImage( block, options );

	if ( ! image || prefersReducedMotion() ) {

		return null;

	}

	// Bigger factor = more drift. The scale is what gives the image the headroom
	// the drift travels into; measure() below keeps the two in step.
	const factor = resolveFactor( block, options );
	const scale = Number( ( 1 + factor + 0.02 ).toFixed( 3 ) );

	// The box the image covers and is clipped by — its own container, which is
	// what the scale is measured against. It is the block itself in a full-bleed
	// hero, but a block that insets its photo (frame padding, an inner card) is
	// taller than this box, and sizing the drift off the block would overshoot
	// the headroom and expose a strip of the frame at one edge.
	//
	// offsetParent (not parentElement) on purpose: an absolutely positioned image
	// can sit inside an unstyled wrapper — e.g. a <picture> used for a mobile vs
	// desktop <source> — that has no size of its own, since an absolutely
	// positioned child never contributes to its parent's box. offsetParent skips
	// straight to the actual positioned ancestor the image is measured against
	// (the block's media container), regardless of markup in between.
	const frame = image.offsetParent || image.parentElement || block;

	let visible = false;
	let ticking = false;
	let observer = null;

	// Cached drift budget plus the box sizes it was derived from, so update()
	// can tell when a re-measure is due (viewport resize, a lazy image landing,
	// a font swap reflowing the card) without listening for each of them.
	const geometry = { frameHeight: -1, frameWidth: -1, imageHeight: -1, range: 0 };

	/**
	 * Re-measure how far the image may travel and cache it on `geometry`.
	 *
	 * The travel is the smaller of two things: the intended drift, a share of
	 * the frame so the effect reads the same in every block, and the slack the
	 * scaled-up image actually has at its tighter edge. An image that fails to
	 * cover its frame at rest — the CSS contract broken, or an upload too small
	 * to crop — measures no slack and simply does not drift, rather than pumping
	 * a gap in and out of view as the page scrolls.
	 */
	const measure = () => {

		// Read the layout boxes, not the painted ones: the running transform
		// would otherwise fold into the very numbers it is derived from. The
		// clear and the restore sit in one task, so nothing paints between them.
		const applied = image.style.transform;

		image.style.transform = 'none';

		const frameRect = frame.getBoundingClientRect();
		const imageRect = image.getBoundingClientRect();

		image.style.transform = applied;

		// Each edge of the image grows out by half of what the scale adds.
		const top = imageRect.top - frameRect.top;
		const grown = ( ( scale - 1 ) * imageRect.height ) / 2;
		const headroom = Math.min(
			grown - top,
			top + imageRect.height + grown - frameRect.height,
		);

		geometry.frameHeight = frame.offsetHeight;
		geometry.frameWidth = frame.offsetWidth;
		geometry.imageHeight = image.offsetHeight;
		geometry.range = Math.max(
			0,
			Math.min( frameRect.height * factor, headroom * 2 ),
		);

	};

	/**
	 * Whether either box has changed size since the cached measurement.
	 *
	 * @return {boolean} True when the drift budget has to be measured again.
	 */
	const isStale = () =>
		frame.offsetHeight !== geometry.frameHeight ||
		frame.offsetWidth !== geometry.frameWidth ||
		image.offsetHeight !== geometry.imageHeight;

	/**
	 * Recompute and apply the image transform for the current scroll position.
	 */
	const update = () => {

		ticking = false;

		const rect = block.getBoundingClientRect();
		const viewport = window.innerHeight || document.documentElement.clientHeight;

		if ( isStale() ) {

			measure();

		}

		// 0 as the block enters from the bottom → 1 as it leaves past the top.
		const progress = ( viewport - rect.top ) / ( viewport + block.offsetHeight );
		const offset = ( 0.5 - progress ) * geometry.range;

		image.style.transform = `translate3d(0, ${ offset.toFixed( 2 ) }px, 0) scale(${ scale })`;

	};

	/**
	 * Queue a single update on the next animation frame.
	 */
	const requestUpdate = () => {

		if ( ! ticking && visible ) {

			ticking = true;
			window.requestAnimationFrame( update );

		}

	};

	if ( 'IntersectionObserver' in window ) {

		observer = new IntersectionObserver(
			( entries ) => {

				visible = entries[ 0 ].isIntersecting;

				if ( visible ) {

					requestUpdate();

				}

			},
			{ threshold: 0 },
		);

		observer.observe( block );

	} else {

		visible = true;

	}

	// Subscribe to the page-wide scroll/resize manager (window.wombat, set up in
	// front-scripts.js) so N parallax instances on one page share a single
	// native listener instead of adding one each.
	const unsubscribeScroll = window.wombat.scrollManager.addListener( requestUpdate );
	const unsubscribeResize = window.wombat.resizeManager.addListener( requestUpdate );
	update();

	/**
	 * Tear the effect down: stop observing, drop listeners and clear the inline
	 * transform so the element falls back to its CSS state.
	 */
	const destroy = () => {

		if ( observer ) {

			observer.disconnect();

		}

		unsubscribeScroll();
		unsubscribeResize();
		image.style.transform = '';

	};

	return { destroy };

};

/**
 * Convention-based initialiser: find every matching wrapper and wire it up.
 *
 * Reads per-block config from data attributes, so a future block needs no
 * bespoke JS — just the markup contract (`js-parallax` wrapper + a resolvable
 * image + optional `data-parallax-speed`).
 *
 * @param {Object}     [options]               Configuration.
 * @param {ParentNode} [options.root]          Scope to search within; default document.
 * @param {string}     [options.selector]      Wrapper selector; default '.js-parallax'.
 * @param {string}     [options.imageSelector] Fallback image selector for the wrappers.
 * @param {Object}     [options.factors]       Level→factor map forwarded to each block.
 * @param {number}     [options.defaultLevel]  Fallback level forwarded to each block.
 * @return {Array<{ destroy: Function }>} Handles for every initialised block.
 */
export const initParallaxScan = ( options = {} ) => {

	const root = options.root || document;
	const selector = options.selector || '.js-parallax';

	const handles = [];

	root.querySelectorAll( selector ).forEach( ( block ) => {

		const handle = initImageParallax( block, {
			imageSelector: options.imageSelector,
			factors: options.factors,
			defaultLevel: options.defaultLevel,
		} );

		if ( handle ) {

			handles.push( handle );

		}

	} );

	return handles;

};
